import { baseApi } from './baseApi';
import { ServiceTypes } from '../constants';

const mapApiToStore = (item: IGroupFromApi): IMappedGroup => ({
  id: item.ID,
  name: item.Name,
  updatedAt: item.UpdatedAt,
  createdAt: item.CreatedAt,
  deletedAt: item.DeletedAt,
});

export const groupsService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getGroups: builder.query<IMappedGroup[], void>({
      query: () => `/groups`,
      transformResponse: (result: any) => result.data.map(mapApiToStore),
      providesTags: () => [{ type: ServiceTypes.GROUPS, id: 'LIST' }],
    }),
    getGroupsByMe: builder.query<IMappedGroup[], void>({
      query: () => `/groups/me`,
      transformResponse: (result: any) => result.data.map(mapApiToStore),
      providesTags: () => [{ type: ServiceTypes.GROUPS, id: 'LIST' }],
    }),
    getGroupById: builder.query<IMappedGroup, { groupId: number }>({
      query: ({ groupId }) => `/groups/${groupId}`,
      transformResponse: (result: any) => mapApiToStore(result.data),
      providesTags: (result, error, { groupId }) => [{ type: ServiceTypes.GROUPS, id: groupId }],
    }),
    getGroupUsers: builder.query<IGroupUsersFromApi[], { groupId: number }>({
      query: ({ groupId }) => `/groups/${groupId}/users`,
      transformResponse: (result: INgApiResponse<IGroupUsersFromApi[]>) => result.data,
      providesTags: (result, error, { groupId }) => [{ type: ServiceTypes.GROUPS, id: `${groupId}_USERS` }],
    }),
    createGroup: builder.mutation<void, { data: Partial<IGroupFromApi> }>({
      query: ({ data }) => ({
        url: `/groups`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: () => [{ type: ServiceTypes.GROUPS, id: 'LIST' }],
    }),
    updateGroup: builder.mutation<void, { groupId: number; data: Partial<IGroupFromApi> }>({
      query: ({ data, groupId }) => ({
        url: `/groups/${groupId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: ServiceTypes.GROUPS, id: 'LIST' },
        { type: ServiceTypes.GROUPS, id: groupId },
      ],
    }),
    deleteGroup: builder.mutation<void, { groupId: number }>({
      query: ({ groupId }) => ({
        url: `/groups/${groupId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: ServiceTypes.GROUPS, id: 'LIST' },
        { type: ServiceTypes.GROUPS, id: groupId },
      ],
    }),
    detachUserFromGroup: builder.mutation<void, { groupId: number; userId: number }>({
      query: ({ groupId, userId }) => ({
        url: `/groups/${groupId}/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: ServiceTypes.GROUPS, id: `${groupId}_USERS` }],
    }),
    attachUserToGroup: builder.mutation<void, { groupId: number; userId: number }>({
      query: ({ groupId, userId }) => ({
        url: `/groups/${groupId}/users`,
        method: 'POST',
        body: {
          user_id: userId,
        },
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: ServiceTypes.GROUPS, id: `${groupId}_USERS` }],
    }),
  }),
});

export const {
  useGetGroupsQuery,
  useGetGroupByIdQuery,
  useGetGroupsByMeQuery,
  useGetGroupUsersQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,
  useDetachUserFromGroupMutation,
  useAttachUserToGroupMutation,
} = groupsService;
